<script>
//Repositories
import Repositories from '@/repositories';
const ItemsRepository = Repositories.get('items');

//Mixins
import textMixin from '@/mixins/textMixin';
import statusMixin from '@/mixins/statusMixin';

//Components
import GridComponent from '@/components/widgets/grids/api-linked-grid';
import StatusComponent from '@/components/widgets/badges/status';

//Modals
import ModalLayout from '@/router/layouts/modal-view.vue';
import ItemInfoModal from '../../management/main.vue';
import ModifyItemModal from '../../shared/modals/modify-item.vue';
import ModifyAllocationModal from '../../shared/modals/update-allocation.vue';

export default {
  props: {
    filters: Object,
    fields: Array,
  },
  components: {
    GridComponent,
    StatusComponent,
    ModalLayout,
    ItemInfoModal,
    ModifyItemModal,
    ModifyAllocationModal,
  },
  mixins: [textMixin, statusMixin],
  data() {
    return {
      inventoryList: [],
      allocationsList: [],
      fullListSize: 0,
      gridContext: null,
      isLoading: false,
      itemForModal: null,
      lastClickedItem: null,
      isLoadingAllocations: [],
      isErrorAllocations: [],
    };
  },
  created() {
    this.gridContext = {
      currentPage: 1,
      perPage: this.itemsPerPage,
      sortBy: 'statusOrder',
      sortDesc: false,
    };

    this.loadData();
  },
  methods: {
    loadData() {
      this.isLoading = true;

      ItemsRepository.getAll({
        ...this.filters,
        ...this.gridContext,
      })
        .then(({ data }) => {
          if (data != null && data.data != null) {
            this.inventoryList = data.data;
            this.fullListSize = data.count;
          }
        })
        .catch(() => {
          this.$toast.errorToast(this.$t('errors.errorLoading'));
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    itemUpdated(item) {
      let foundIndex = this.inventoryList.findIndex(
        (x) => x.idPublic == item.idPublic
      );

      this.inventoryList = [
        ...this.inventoryList.slice(0, foundIndex),
        item,
        ...this.inventoryList.slice(foundIndex + 1),
      ];

      this.itemForModal = item;
    },
    contextChanged(context) {
      this.gridContext = context;
      this.loadData();
    },
    openModal(item, modalName) {
      this.itemForModal = item;
      this.lastClickedItem = modalName + '-' + item.idPublic;

      this.$nextTick(() => this.$bvModal.show(modalName));
    },
    modalClosed() {
      this.itemForModal = null;
      this.loadData();
    },
  },
  computed: {
    inventoryListComputed() {
      return this.inventoryList.map((x) => {
        let status = this.getStatusFromCode(x.status, 'Inventory');
        return { ...x, ...status };
      });
    },
    itemsPerPage() {
      return this.$store.getters['localstorage/getDynamicConfigs'](
        'gridConfig-inventory-grid'
      ).itemsPerPage;
    },
  },
  watch: {
    filters() {
      this.inventoryList = [];
      this.gridContext.currentPage = 1;
      this.loadData();
    },
  },
};
</script>

<template>
  <b-card no-body class="p-3 custom-inventory-shadow">
    <GridComponent
      v-if="gridContext != null"
      id="inventory-grid"
      :fields="fields"
      :items="inventoryListComputed"
      :nbItems="fullListSize"
      :is-busy="isLoading"
      :default-sort="gridContext.sortBy"
      :current-page="gridContext.currentPage"
      @context-changed="contextChanged"
    >
      <template #cell(code)="data">
        <div
          v-html="
            highlightMatches(data.value, filters != null ? filters.search : '')
          "
        ></div>
      </template>

      <template #cell(name)="data">
        <div
          v-html="
            highlightMatches(data.value, filters != null ? filters.search : '')
          "
        ></div>
      </template>

      <template #cell(orderRequestQuantity)="data">
        {{
          data.item.orderRequestQuantity + data.item.workOrdersRequestsQuantity
        }}
      </template>

      <template #cell(allocationName)="data">
        <div
          @click="openModal(data.item, 'modify-allocation-in-grid-modal')"
          style="cursor: pointer; width: 100%; height: 22px"
        >
          {{ data.item.allocationName }}
        </div>
      </template>

      <template #cell(statusOrder)="data">
        <div>
          <StatusComponent
            :text="data.item.statusName"
            :variant="data.item.statusVariant"
          />
        </div>
      </template>

      <template #cell(buttons)="data">
        <div class="flex-center-align">
          <div
            @click="openModal(data.item, 'modify-items-in-grid-modal')"
            style="cursor: pointer; white-space: nowrap"
          >
            <i
              class="fas fa-minus text-danger pl-2 pt-1 pb-1"
              style="font-size: 1.2em"
            ></i>
            <span class="text-secondary" style="font-weight: 800"> / </span>
            <i
              class="fas fa-plus text-success pr-2 pt-1 pb-1"
              style="font-size: 1.2em"
            ></i>
          </div>
          <div @click.capture.prevent="openModal(data.item, 'item-info-modal')">
            <b-link
              :to="{
                name: 'manageItem',
                params: { idPublic: data.item.idPublic },
              }"
            >
              <i
                v-if="
                  lastClickedItem === 'item-info-modal-' + data.item.idPublic
                "
                class="fas fa-info-circle pl-2 pr-2 pt-1 pb-1"
                style="cursor: pointer; font-size: 1.3em; color: #9799a6"
              ></i>
              <i
                v-else
                class="fas fa-info-circle text-secondary pl-2 pr-2 pt-1 pb-1"
                style="cursor: pointer; font-size: 1.3em"
              ></i>
            </b-link>
          </div>
        </div>
      </template>
    </GridComponent>

    <ModalLayout
      name="item-info-modal"
      @modal-closed="modalClosed"
      :title="
        $t('items.item') + ' ' + (itemForModal != null ? itemForModal.name : '')
      "
      :route="{
        name: 'manageItem',
        params: {
          idPublic: itemForModal != null ? itemForModal.idPublic : '',
        },
      }"
    >
      <div v-if="itemForModal != null">
        <ItemInfoModal
          :itemIdPublic="itemForModal.idPublic"
          @item-updated="itemUpdated"
        />
      </div>
    </ModalLayout>

    <ModifyItemModal
      :item="itemForModal"
      modalId="modify-items-in-grid-modal"
      @item-updated="itemUpdated"
      @modal-closed="modalClosed"
    />

    <ModifyAllocationModal
      :item="itemForModal"
      modalId="modify-allocation-in-grid-modal"
      @item-updated="itemUpdated"
      @modal-closed="modalClosed"
    />
  </b-card>
</template>
